import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzVpbDataService,
  CorporateTaxEntity,
  VerliezenFiscaleEenheid,
  VpbAangifte
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {debounceTime, filter, Observable, shareReplay, take} from "rxjs";
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-fiscale-eenheid-verliezen',
  templateUrl: './fiscale-eenheid-verliezen.component.html',
  styleUrls: ['./fiscale-eenheid-verliezen.component.scss'],
  imports: [
    SharedUiButtonModule,
    ReactiveFormsModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    CommonModule
  ]
})
export class FiscaleEenheidVerliezenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);

  public enkelvoudigFg = new FormGroup({
    specificatieVerliezen: new FormArray<FormGroup>([])
  });

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  get specificatieVerliezen(): FormArray {
    return this.enkelvoudigFg.get('specificatieVerliezen') as FormArray;
  }

  addRij(): void {
    this.specificatieVerliezen.push(this.createFormGroup(), { emitEvent: false });
  }

  verwijderRij(index: number): void {
    this.specificatieVerliezen.removeAt(index, { emitEvent: false });
    this.saveForm();
  }

  createFormGroup(item?: VerliezenFiscaleEenheid): FormGroup {
    return new FormGroup({
      rsinMaatschappijVerlies: new FormControl<string | null>(item?.rsinMaatschappijVerlies ?? null),
      startBoekjaarVerlies: new FormControl<Date | null>(item?.startBoekjaarVerlies ?? null),
      eindBoekjaarVerlies: new FormControl<Date | null>(item?.eindBoekjaarVerlies ?? null),
      teVerrekenenVerlies: new FormControl<number | null>(item?.teVerrekenenVerlies ?? null),
      teVerrekenenVerliesNaarVorigBoekjaar: new FormControl<number | null>(item?.teVerrekenenVerliesNaarVorigBoekjaar ?? null),
    });
  }

  ngOnInit(): void {
    this.corporateTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((data) => {
      this.updateForm(data.aangifte!);
    })
    this.enkelvoudigFg.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.saveForm();
    });
  }

  private updateForm(aangifte: VpbAangifte): void {
    const lijst = aangifte.verliescompensatie?.samenStellingVerliezenFiscaleEenheid ?? [];
    const formArray = this.specificatieVerliezen;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        formArray.at(i).patchValue(lijst[i], {emitEvent: false});
      } else {
        formArray.push(this.createFormGroup(lijst[i]), {emitEvent: false});
      }
    }
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  saveForm(): void {
    this.enkelvoudigFg.updateValueAndValidity({emitEvent: false});

    const samenStellingVerliezenFiscaleEenheid: VerliezenFiscaleEenheid[] =
      this.specificatieVerliezen.getRawValue().filter((row: any) => row.rsinMaatschappijVerlies)
        .map((row: any): VerliezenFiscaleEenheid => ({
          rsinMaatschappijVerlies: row.rsinMaatschappijVerlies ?? undefined,
          startBoekjaarVerlies: row.startBoekjaarVerlies ?? undefined,
          eindBoekjaarVerlies: row.eindBoekjaarVerlies ?? undefined,
          teVerrekenenVerlies: row.teVerrekenenVerlies ?? undefined,
          teVerrekenenVerliesNaarVorigBoekjaar: row.teVerrekenenVerliesNaarVorigBoekjaar ?? undefined,
        }));

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          verliescompensatie: {
            ...currentEntity.aangifte.verliescompensatie,
            samenStellingVerliezenFiscaleEenheid,
          }
        } as VpbAangifte,
      };
    });
  }
}
